import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import {
  Observable,
  BehaviorSubject,
  throwError,
} from 'rxjs';
import {
  catchError,
  filter,
  switchMap,
  take,
} from 'rxjs/operators';
import { TokenStorageService } from './token-storage.service';
import { AuthenticationService } from './authentication.service';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { SnackbarService } from '../../../myt/utils/services/snackbar.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<boolean | null>(null);
  private refreshFailed = false; // 🚨 once true, no more refresh attempts

  constructor(
    private tokenStorageService: TokenStorageService,
    private snackbarService: SnackbarService,
    private authService: AuthenticationService,
    private router: Router
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const isApiPath = request.url.startsWith('/api');
    const requestUrl = isApiPath ? this.addCacheBuster(`${environment.API_URL}${request.url}`) : request.url;
    const authToken = environment.useBearerAuth && isApiPath && typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('authToken') : '';
    let authReq = isApiPath
      ? request.clone({
          withCredentials: environment.useBearerAuth ? false : true,
          ...(authToken && !request.headers.has('Authorization') ? { setHeaders: { Authorization: `Bearer ${authToken}` } } : {}),
          url: requestUrl,
        })
      : request;

    const isRefreshCall = authReq.url.includes('refreshToken');

    // 🔹 1. Handle refreshToken API separately
    if (isRefreshCall) {
      return next.handle(authReq).pipe(
        catchError((error: HttpErrorResponse) => {
          // if refresh itself is 401 -> refresh token is dead -> hard logout
          if (error.status === 401) {
            this.refreshFailed = true;
            this.logout();
          } else {
            this.handleAllErrors(error);
          }
          return throwError(() => error);
        })
      );
    }

    // 🔹 2. Normal APIs
    return next.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status !== 401) {
          this.handleAllErrors(error);
          return throwError(() => error);
        }

        if (environment.useBearerAuth) {
          this.logout('Session expired. Please sign in again.');
          return throwError(() => error);
        }

        // If we already know refresh is impossible or user logged out,
        // don't try refresh again -> prevents infinite loop
        if (this.refreshFailed || !this.authService.isAuthenticated) {
          this.logout();
          return throwError(() => error);
        }

        return this.handle401Error(authReq, next, error);
      })
    );
  }

  private handleAllErrors(error: HttpErrorResponse) {
    const loginPath = '/login';

    if (error.error && error.error.message) {
      this.snackbarService.openSnackbar({
        message: `${error.error.message}`,
        type: 'error',
        duration: 5000,
      });
    } else if (error.error && error.error.errorResponse) {
      this.snackbarService.openSnackbar({
        message: `${error.error.errorResponse}`,
        type: 'error',
        duration: 5000,
      });
    } else if (error.message) {
      this.snackbarService.openSnackbar({
        message: `${error.message}`,
        type: 'error',
        duration: 5000,
      });
    } else {
      this.snackbarService.openSnackbar({
        message: 'Server Issue. Please try after sometime!!',
        type: 'error',
        duration: 5000,
      });
    }

    if (error.status !== 401 || window.location.pathname === loginPath) {
      return;
    }
  }

  private handle401Error(
    req: HttpRequest<any>,
    next: HttpHandler,
    originalError: HttpErrorResponse
  ): Observable<HttpEvent<any>> {
    if (this.isRefreshing) {
      // other requests wait until refresh completes
      return this.refreshTokenSubject.pipe(
        filter((v) => v !== null),
        take(1),
        switchMap((success) => {
          if (success) {
            // retry original request once
            const retried = req.clone({
              setHeaders: { 'X-Refresh-Attempted': 'true' },
            });
            return next.handle(retried);
          } else {
            return throwError(() => originalError);
          }
        })
      );
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    return this.authService.refreshToken().pipe(
      switchMap((response: any) => {
        this.isRefreshing = false;

        if (response?.status === 'Success' && response.data) {
          this.refreshFailed = false; // refresh succeeded
          this.authService.loginSuccess(response.data);

          this.refreshTokenSubject.next(true);

          const retried = req.clone({
            setHeaders: { 'X-Refresh-Attempted': 'true' },
          });
          return next.handle(retried);
        } else {
          this.refreshFailed = true;
          this.refreshTokenSubject.next(false);
          this.logout();
          return throwError(() => originalError);
        }
      }),
      catchError((err) => {
        this.isRefreshing = false;
        this.refreshFailed = true;
        this.refreshTokenSubject.next(false);
        this.logout();
        return throwError(() => err);
      })
    );
  }

  private logout(message = '') {
    this.tokenStorageService.signOut();
    this.authService.isAuthenticated = false;
    if (message && window.location.pathname !== '/login') this.snackbarService.openSnackbar({ message, type: 'error', duration: 5000 });
    this.router.navigate(['/login']);
  }

  private addCacheBuster(url: string): string {
    const timestamp = new Date().getTime();
    if (url.indexOf('?') > -1) {
      return `${url}&cb=${timestamp}`;
    } else {
      return `${url}?cb=${timestamp}`;
    }
  }
}
